import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Driver, driver } from "driver.js";
import "driver.js/dist/driver.css";
import { Tour, TourPanel, tourTargetSelector, visibleSteps, waitForTarget } from "./tours";
import { DriverTourStyles } from "./Styles";

const PANEL_OPEN_WAIT_MS = 2000;

export interface TourRunnerProps {
  tour: Tour;
  /** Called once when the user finishes or dismisses the tour, not when the runner unmounts. */
  onFinish: () => void;
}

/**
 * Runs a tour with driver.js.
 *
 * @param tour The tour to run.
 * @param onFinish What to do once the user is done with it.
 */
export const DriverTourRunner = ({ tour, onFinish }: TourRunnerProps) => {
  const { t } = useTranslation();
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    const steps = visibleSteps(tour.steps);
    if (steps.length === 0) return;

    let openPanel: TourPanel | undefined;
    let isMoving = false;

    const closePanel = () => {
      openPanel?.close();
      openPanel = undefined;
    };

    // A panel is opened before moving, so driver.js finds its targets when it
    // looks for them; once open, the steps whose target is not in it (an item
    // the user does not get) are skipped right away.
    const moveBy = async (activeDriver: Driver, direction: 1 | -1) => {
      if (isMoving) return;
      const nextPanel = steps[(activeDriver.getActiveIndex() ?? 0) + direction]?.panel;
      if (nextPanel && nextPanel !== openPanel) {
        isMoving = true;
        closePanel();
        nextPanel.open();
        openPanel = nextPanel;
        await waitForTarget(nextPanel.target, PANEL_OPEN_WAIT_MS);
        isMoving = false;
      }
      if (direction === 1) activeDriver.moveNext();
      else activeDriver.movePrevious();
    };

    const tourDriver = driver({
      steps: steps.map((step, index) => ({
        element: step.target ? tourTargetSelector(step.target) : undefined,
        popover: {
          title: t(step.title),
          description: t(step.body),
          side: step.side,
          align: step.align,
          // Not left to driver.js, which would call the step before a closed
          // panel the last one, as it finds none of the panel's targets yet.
          nextBtnText: index < steps.length - 1 ? t("common.tour.next") : t("common.tour.done"),
        },
        skipMissingElement: step.panel ? true : undefined,
      })),
      popoverClass: "mike-tour",
      // The highlighted nav buttons would otherwise navigate away mid-tour.
      disableActiveInteraction: true,
      // A click on the dark backdrop is too easy to make by accident to count as dismissing the tour.
      overlayClickBehavior: "none",
      showProgress: true,
      // driver.js fills in its own {{current}} and {{total}}, so i18next has to leave them as they are.
      progressText: t("common.tour.progress", { current: "{{current}}", total: "{{total}}" }),
      prevBtnText: t("common.tour.previous"),
      closeBtnLabel: t("common.tour.close"),
      onNextClick: (_element, _step, { driver: activeDriver }) => void moveBy(activeDriver, 1),
      onPrevClick: (_element, _step, { driver: activeDriver }) => void moveBy(activeDriver, -1),
      // Wherever driver.js lands, skipped steps included, a panel the step is not in goes away.
      onHighlightStarted: (_element, _step, { driver: activeDriver }) => {
        if (steps[activeDriver.getActiveIndex() ?? 0]?.panel !== openPanel) closePanel();
      },
      // Only the user's way out (done, X, Escape) goes through
      // here, and from the first instant: onDestroyed would also fire on our
      // own destroy() below, and miss a close before the first step settles.
      onDestroyStarted: () => {
        closePanel();
        tourDriver.destroy();
        onFinishRef.current();
      },
    });

    tourDriver.drive();

    return () => {
      closePanel();
      tourDriver.destroy();
    };
  }, [tour, t]);

  return <DriverTourStyles />;
};
